import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';

import { useFormik } from 'formik';
import { camelizeKeys } from 'humps';
import { GoogleLoginResponse } from 'react-google-login';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { theme } from '@waglabs/base-theme';
import { Box } from '@waglabs/box-next';
import { Flex } from '@waglabs/flex-next';
import { Image } from '@waglabs/image-next';
import { TextField } from '@waglabs/textfield';
import { Typography } from '@waglabs/typography';
import { useToggle } from '@waglabs/use-toggle';

import { resetRequest } from '../../../../../../actions/requests';
import { SignInWithAppleButton } from '../../../../../../components/AppleLoginButton/SignInWithAppleButton';
import { DividerWithElement } from '../../../../../../components/DividerWithElement';
import { SignInWithGoogleButton } from '../../../../../../components/GoogleLoginButton/SignInWithGoogleButton';
import {
  NAME_ENTER_OWNER_INFO,
  NAME_OWNER_INFO_ENTERED,
} from '../../../../../../components/Tracker/Segment/events';
import {
  DataTestIds,
  DRAFT_SIGNUP_BASIC_INFO_FORM,
  OWNER_REGISTRATION_BASIC_INFO_REQUEST_KEY,
  QS_AD_CREATIVE,
  QS_AD_GROUP,
  QS_GCLID,
  QS_PROMO_CODE_KEY,
  ENV_APP_COOKIE_SUBDOMAIN,
  STATUS_CODE_CONFLICT,
  Terms,
  utmQueryStringKeys,
  Time,
  Routes,
  CookieKeys,
} from '../../../../../../constants';
import {
  useSegmentIdentify,
  useSegmentTrack,
  useDraft,
  useGetUrlQuery,
  useIsInEmbed,
  useSession,
} from '../../../../../../hooks';
import {
  useAppleLoginWithAuthentication,
  useFacebookLoginWithAuthentication,
} from '../../../../../../hooks/api';
import { useGetWalkerFromUrlQuery } from '../../../../../../hooks/useGetWalkerFromUrlQuery';
import { usePostGoogleSocialAuthenticationMutation } from '../../../../../../slices/auth/socialAuthApi';
import { ownerSlice } from '../../../../../../slices/owners/owner';
import { usePostRegisterOwnerMutation } from '../../../../../../slices/owners/v6';
import { petsSlice } from '../../../../../../slices/pets';
import { wagApi } from '../../../../../../types/wagapi';
import {
  emailSchema,
  getFormikFieldHelper,
  isInitialValid,
  phoneSchema,
  pickQueryStrings,
} from '../../../../../../utils';
import { ApiErrorV6 } from '../../../../../../utils/buildApiErrorPipeline';
import { setCookie } from '../../../../../../utils/setCookie';
import { tryCatch } from '../../../../../../utils/tryCatch';
import { LoadingBoneOverlay } from '../../../../../OwnerRegistration/components/LoadingBoneOverlay';
import { StepContainer } from '../StepContainer';
import { StepFooter } from '../StepFooter';

import { ConflictModal } from './components/ConflictModal';
import { SocialSigninOptions } from './components/SocialSigninOptions';
import PhoneInputWithCountrySelect from 'react-phone-number-input';
import { PhoneTextField } from '../../../../../../components/PhoneTextField';

const FormFields = Object.freeze({
  FirstName: 'firstName',
  LastName: 'lastName',
  Email: 'email',
  PhoneNumber: 'phoneNumber',
});

const FormFieldLabels = Object.freeze({
  Email: 'Email',
  PhoneNumber: 'Phone number',
  FirstName: 'First name',
  LastName: 'Last name',
});

// Form schema used when signing up via email
const emailFormValidationSchema = Yup.object({
  [FormFields.Email]: emailSchema,
  [FormFields.PhoneNumber]: phoneSchema,
  [FormFields.FirstName]: Yup.string()
    .label(FormFieldLabels.FirstName)
    .required(),
  [FormFields.LastName]: Yup.string()
    .label(FormFieldLabels.LastName)
    .required(),
});

export const OwnerInfo = () => {
  /**
   * If we have originally entered the flow
   * by logging in with a social auth provider
   * the user shouldn't be prompted for that info
   * again or to login with a social auth provider again.
   */
  const ownerId = useSelector(ownerSlice.selectors.getOwnerID);
  const ownerToken = useSelector(ownerSlice.selectors.getOwnerToken);

  const {
    push,
    location,
  } = useHistory();

  const isInEmbed = useIsInEmbed();
  const walker = useGetWalkerFromUrlQuery();

  const getIsLinkSocialLoginFlow = () => {
    if (ownerToken) {
      return true;
    }
    return false;
  };

  // begin hook definitions for component
  const {
    on: showSignupForm,
    toggle: toggleShowSignupForm,
  } = useToggle({ initialValue: true });

  const {
    on: isSocialLoginLinkFlow,
  } = useToggle({ initialValue: getIsLinkSocialLoginFlow() });

  /**
   * On page load, we are already auto loading Google Sign In,
   * wait for the `onAutoLoadFinished` to toggle it to another state
   */
  const [
    isLoadingGoogleSignIn,
    setIsLoadingGoogleSignIn,
  ] = useState(!isSocialLoginLinkFlow);

  /**
   * Attribution
   */
  const queryParams = useGetUrlQuery();
  const utmAttributions = camelizeKeys(pickQueryStrings([
    ...utmQueryStringKeys,
  ], queryParams));
  const attributions = pickQueryStrings([
    QS_GCLID,
    QS_AD_GROUP,
    QS_AD_CREATIVE,
  ], queryParams);
  const promoCodeAttribution = camelizeKeys(pickQueryStrings([
    QS_PROMO_CODE_KEY,
  ], queryParams));

  const [socialSignInStarted, setSocialSignInStarted] = useState(false);
  const [googleToken, setGoogleToken] = useState(null);
  const [googleLoginResponse, setGoogleLoginResponse] = useState(null);

  const dispatch = useDispatch();

  const [
    executePostGoogleSocialAuthentication,
    {
      isLoading: googleAuthenticationIsLoading,
      isError: googleAuthenticationIsError,
      error: googleAuthenticationError,
    },
  ] = usePostGoogleSocialAuthenticationMutation();

  const {
    appleAuthenticationSubmitting,
    executeAppleLoginWithAuthentication,
  } = useAppleLoginWithAuthentication();

  const {
    facebookAuthenticationSubmitting,
    execute: executeFacebookLoginWithAuthentication,
  } = useFacebookLoginWithAuthentication();

  const {
    execute: segmentIdentify,
  } = useSegmentIdentify();

  const {
    execute: segmentTrack,
  } = useSegmentTrack();

  const notActivePets = useSelector(petsSlice.selectors.getAllNotActive);

  const {
    addDraft,
    clearDraft,
    draft,
  } = useDraft(DRAFT_SIGNUP_BASIC_INFO_FORM);

  const {
    clear: clearSession,
  } = useSession();

  const [
    executePostRegisterOwner,
    {
      isLoading: isPostRegisterOwnerLoading,
      error: postRegisterOwnerError,
    },
  ] = usePostRegisterOwnerMutation();

  // end hook definitions for component

  // begin helper functions for component

  // There are no fields when signing a user up through social, so we have a blank Yup object to ensure
  // that the Continue button is automatically enabled
  const socialSignInValidationSchema = Yup.object();
  const validationSchema = socialSignInStarted ? socialSignInValidationSchema : emailFormValidationSchema;
  const {
    errors,
    isSubmitting,
    isValid,
    touched,
    values,
    handleBlur,
    setFieldValue,
    handleChange,
    handleSubmit,
  } = useFormik({
    isInitialValid,
    initialValues: draft || validationSchema.cast(undefined),
    validationSchema,
    onSubmit: async (submittedValues) => {
      /**
       * If ownerId & ownerToken are available, the user is already registered via an auth provider.
       * We don't need to make an API request to register the owner.
       */
      if (!ownerId && !ownerToken) {
        // Regular email signup logic
        const [registerOwnerResponse, err] = await tryCatch(() => executePostRegisterOwner({
          ...submittedValues,
          attribution: {
            ...attributions,
            ...utmAttributions,
          },
          ...promoCodeAttribution,
        }).unwrap()) as [
            wagApi.V6.PostRegisterOwnerResponse,
            ApiErrorV6,
          ];

        if (err) {
          return;
        }
        const {
          id,
          token,
        } = registerOwnerResponse;

        // TODO set in redux after main signup flow is complete
        setCookie(CookieKeys.OwnerAuthToken, token, ENV_APP_COOKIE_SUBDOMAIN, Time.ThirtyMinutes);
        setCookie(CookieKeys.OwnerIdentifier, id, ENV_APP_COOKIE_SUBDOMAIN, Time.ThirtyMinutes);

        trackEvents(ownerId);
      } else {
        trackEvents(ownerId);
      }

      clearDraft();

      push({
        pathname: Routes.SignupPetInfoPart1,
        search: location.search,
      });
    },
  });

  const formikFieldHelper = getFormikFieldHelper({
    errors,
    touched,
  });

  const firstNameField = formikFieldHelper(FormFields.FirstName);
  const emailField = formikFieldHelper(FormFields.Email);
  const phoneField = formikFieldHelper(FormFieldLabels.PhoneNumber);
  const lastNameField = formikFieldHelper(FormFields.LastName);

  /**
   * When inEmbed, do not display bone spinner on "page load" to avoid conflicting bone spinner
   * We are already disabling the interactions so it is fine to remove the `isLoadingGoogleSignIn`
   * when in embed mode.
   *
   * We have an `onLoad` event handler when embedding the app and that handles loading spinner
   * ahead of time
   */
  const isSignupFetching = isInEmbed
    ? (
      googleAuthenticationIsLoading
      || facebookAuthenticationSubmitting
      || appleAuthenticationSubmitting
      || isPostRegisterOwnerLoading
    )
    : (
      googleAuthenticationIsLoading
      || isLoadingGoogleSignIn
      || facebookAuthenticationSubmitting
      || appleAuthenticationSubmitting
      || isPostRegisterOwnerLoading
    );

  const disableInteractions = isSignupFetching || isSubmitting;

  const handleOnGoogleLoginSuccess = async (googleResponse: GoogleLoginResponse) => {
    const idToken = googleResponse.getAuthResponse().id_token;

    setGoogleToken(idToken);
    setGoogleLoginResponse(googleResponse);

    // Send the idToken to the backend v6 endpoint
    const [response, error] = await tryCatch(() => (
      executePostGoogleSocialAuthentication({
        accessToken: idToken,
        attribution: {
          ...attributions,
          ...utmAttributions,
        },
        ...promoCodeAttribution,
      }).unwrap()));

    if (error) {
      handleSocialProviderError(error);
      return;
    }

    handleSocialProviderResponse(response);
  };

  const signupErrorMessage = (postRegisterOwnerError as ApiErrorV6)?.message;

  const handleAppleLogin = async () => {
    const attributionParams = {
      attribution: {
        ...attributions,
        ...utmAttributions,
      },
      ...promoCodeAttribution,
    };

    const [response, error] = await tryCatch(() => executeAppleLoginWithAuthentication(attributionParams));

    if (error) {
      handleSocialProviderError(error);
    }

    if (response) {
      handleSocialProviderResponse(response);
    }
  };

  const handleFacebookLogin = async () => {
    const attributionParams = {
      attribution: {
        ...attributions,
        ...utmAttributions,
      },
      ...promoCodeAttribution,
    };

    const [response, error] = await tryCatch(() => executeFacebookLoginWithAuthentication(attributionParams));

    if (error) {
      handleSocialProviderError(error);
    }
    if (response) {
      handleSocialProviderResponse(response);
    }
  };

  const handleSocialProviderError = (error: ApiErrorV6) => {
    const {
      status,
    } = error;

    if (status === STATUS_CODE_CONFLICT) {
      setIsConflictModalOpen(true);

      if (isInEmbed) {
        clearDraft();

        push({
          pathname: Routes.SignupOwnerExists,
          search: location.search,
        });
      }
    }
  };

  const handleSocialProviderResponse = (response: wagApi.PostSocialAuthenticationResponse) => {
    const {
      newAccount,
    } = response;

    if (newAccount) {
      setSocialSignInStarted(true);
      toggleShowSignupForm();

      return;
    }

    if (isInEmbed) {
      window.open(Routes.Login, '_blank');
    } else {
      push({
        pathname: Routes.Home,
      });
    }
  };

  // end helper functions for component

  // begin hook based functions for component: useEffect/useCallback/useState/useMemo etc.

  /**
   * Calls tracking events for Segment and also
   * identifies the newly registered owner in Segment.
   */
  const trackEvents = (ownerId: number) => {
    segmentIdentify({
      id: ownerId,
    });

    const {
      firstName,
      lastName,
      email,
      phone,
    } = values;

    segmentTrack({
      event: NAME_OWNER_INFO_ENTERED,
      data: {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
      },
    });
  };

  useEffect(() => {
    // Track page mount event
    segmentTrack({
      event: NAME_ENTER_OWNER_INFO,
    });
  }, []);

  useEffect(() => {
    dispatch(resetRequest(OWNER_REGISTRATION_BASIC_INFO_REQUEST_KEY));
  }, [dispatch]);

  const hasTouchedForm = useMemo(() => (
    !!Object.keys(touched).length
  ), [
    touched,
  ]);

  useEffect(() => {
    if (!hasTouchedForm) {
      return;
    }
    // Save draft of form values
    addDraft(values);
  }, [
    addDraft,
    values,
    hasTouchedForm,
  ]);

  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);

  // end hook based functions for component

  const subtitle = isInEmbed
    ? `Sign up to connect with ${walker.name}`
    : 'Sign up to stay up to date on your future requests.';

  return (
    <>
      {isSignupFetching && (
        <LoadingBoneOverlay />
      )}

      <ConflictModal
        isConflictModalOpen={isConflictModalOpen}
        setIsConflictModalOpen={setIsConflictModalOpen}
        push={push}
      />

      <StepContainer>
        {walker.isValid && (
          <Box
            textAlign="center"
          >
            <Image
              src={walker.imageUrl}
              alt={walker.name}
              height="100px"
              width="100px"
              borderRadius="50px"
            />

            <Typography
              color={theme.colors.dimGray}
              fontFamily="muliBold"
              fontSize="18px"
              mt="8px"
            >
              {walker.name}
            </Typography>
          </Box>
        )}

        <Typography
          variant="h1"
          color={theme.colors.charcoal}
          fontSize="30px"
          m={0}
          textAlign="center"
        >
          Welcome to Wag!
        </Typography>

        <Typography
          variant="h2"
          color={theme.colors.dimGray}
          fontSize="18px"
          m={0}
          textAlign="center"
        >
          {subtitle}
        </Typography>

        <SocialSigninOptions
          mt="36px"
          mb="24px"
          // Unusual implementation of the Google button svg requires this margin
          // to center and space the buttons accordingly
          marginLeft="-10px"
          display="grid"
          justifyContent="center"
          gridGap="10px"
        >
          <SignInWithGoogleButton
            my="10px"
            marginRight="4px"
            clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}
            buttonText={googleToken ? 'Signed in' : 'Sign in with Google'}
            data-testid={DataTestIds.Login.loginForm.SignInWithGoogleButton}
            onSuccess={handleOnGoogleLoginSuccess}
            setIsLoadingGoogleSignIn={setIsLoadingGoogleSignIn}
          />
          <SignInWithAppleButton
            my="10px"
            onClick={handleAppleLogin}
          />
        </SocialSigninOptions>

        <Box>
          {showSignupForm && (
            <DividerWithElement
              mt="8px"
              mb="18px"
            >
              <Box
                fontSize={['16px, 24px']}
              >
                OR
              </Box>
            </DividerWithElement>
          )}

          <form
            onSubmit={handleSubmit}
          >
            <Flex
              flexDirection={['column', null, 'row']}
              flexWrap={[null, null, 'wrap']}
              justifyContent="space-between"
              my="24px"
            >
              {showSignupForm && (
                <>
                  <Box
                    my={['8px', null, '8px']}
                    mr={[null, null, '8px']}
                    width={[null, null, 'calc(50% - 8px)']}
                  >
                    <TextField
                      fullWidth
                      error={firstNameField.hasError}
                      helperText={firstNameField.errorMessage}
                      label={FormFieldLabels.FirstName}
                      name={FormFields.FirstName}
                      value={values[FormFields.FirstName]}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      inputProps={{
                        fontFamily: 'muli',
                      }}
                      data-testid={DataTestIds.Signup.OwnerInfoForm.FirstName}
                    />
                  </Box>
                  <Box
                    my={['8px', null, '8px']}
                    width={[null, null, 'calc(50% - 8px)']}
                  >
                    <TextField
                      fullWidth
                      error={lastNameField.hasError}
                      helperText={lastNameField.errorMessage}
                      label={FormFieldLabels.LastName}
                      name={FormFields.LastName}
                      value={values[FormFields.LastName]}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      inputProps={{
                        fontFamily: 'muli',
                      }}
                      data-testid={DataTestIds.Signup.OwnerInfoForm.LastName}
                    />
                  </Box>

                  <Box
                    my={['8px', null, '8px']}
                    mr={[null, null, '8px']}
                    width={[null, null, 'calc(50% - 8px)']}
                  >
                    <TextField
                      fullWidth
                      type="email"
                      error={emailField.hasError}
                      helperText={emailField.errorMessage}
                      label={FormFieldLabels.Email}
                      name={FormFields.Email}
                      value={values[FormFields.Email]}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      inputProps={{
                        fontFamily: 'muli',
                      }}
                      data-testid={DataTestIds.Signup.OwnerInfoForm.Email}
                    />
                  </Box>

                  <Box
                    my={['8px', null, '8px']}
                    width={[null, null, 'calc(50% - 8px)']}
                  >
                    <PhoneTextField
                      fullWidth
                      type="phone"
                      error={phoneField.hasError}
                      helperText={phoneField.errorMessage}
                      label={FormFieldLabels.PhoneNumber}
                      name={FormFields.PhoneNumber}
                      variant="outlined"
                      backgroundColor="white"
                      containerProps={{
                        border: 'unset',
                      }}
                      style={{
                        // This will be applied to the PhoneInput
                        // not on the TextField
                        backgroundColor: 'white',
                        border: '1px solid',
                        borderColor: formikFieldHelper(FormFields.Phone)
                          .hasError
                          ? 'red'
                          : '#D1D1D1',
                      }}
                      value={values[FormFields.PhoneNumber]}
                      onBlur={handleBlur}
                      onChange={(phone: string) => {
                        setFieldValue(
                          FormFields.PhoneNumber,
                          phone,
                        )
                      }}
                      inputProps={{
                        fontFamily: 'muli',
                      }}
                      data-testid={DataTestIds.Signup.OwnerInfoForm.Phone}
                    />
                  </Box>
                </>
              )}

              <Typography
                variant="legal"
                data-testid={DataTestIds.TermsAndConditions.Checkboxes.TermsConditionsCopy}
              >
                {Terms.Terms1}
              </Typography>
            </Flex>

            {signupErrorMessage && (
              <Typography
                color={theme.colors.wildWatermelon}
                mb={0}
                mt="16px"
                textAlign="center"
                data-testid={DataTestIds.Signup.OwnerInfoForm.ErrorText}
              >
                {signupErrorMessage}
              </Typography>
            )}

            <StepFooter
              primaryCtaText="Continue"
              primaryCtaProps={{
                type: 'submit',
                disabled: disableInteractions || !isValid,
                'data-testid': DataTestIds.Signup.OwnerInfoForm.SignUpWithEmailBtn,
              }}
            />
          </form>
        </Box>
      </StepContainer>
    </>
  );
};
